import React, { useCallback, useEffect, useRef } from "react";

import { toast } from "@needl-ai/common/components/ui/toaster";

import { useAutoFocus } from "@/hooks/useAutoFocus";

interface ChatTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  canSend?: boolean; // Whether sending is allowed (different from disabled for typing)
  placeholder?: string;
}

const ChatTextarea: React.FC<ChatTextareaProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  canSend = true, // Default to true for backward compatibility
  placeholder = "How can I help you today?",
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoFocusRef = useAutoFocus<HTMLTextAreaElement>(!disabled);

  // Callback ref to handle both textareaRef and autoFocusRef
  const setTextareaRef = useCallback(
    (element: HTMLTextAreaElement | null) => {
      // Use object assignment instead of direct property assignment
      (
        textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
      ).current = element;
      autoFocusRef(element);
    },
    [autoFocusRef]
  );

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && canSend) {
        onSend();
      } else if (!canSend && !disabled) {
        toast({
          description:
            "Please wait for the current response to complete before asking a new question.",
          duration: 3000,
          className:
            "!fixed !top-4 !left-1/2 !-translate-x-1/2 !w-auto !max-w-md data-[state=open]:!animate-in data-[state=open]:!slide-in-from-top-8 data-[state=open]:!fade-in-0 data-[state=open]:!duration-300 data-[state=closed]:!animate-out data-[state=closed]:!slide-out-to-top-8 data-[state=closed]:!fade-out-0 data-[state=closed]:!duration-200 sm:data-[state=open]:!slide-in-from-top-8 sm:data-[state=closed]:!slide-out-to-top-8",
          style: {
            pointerEvents: "none",
          },
        });
      }
    }
  };

  return (
    <>
      <textarea
        ref={setTextareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full resize-none border-none outline-none min-h-[40px] max-h-[5rem] text-sm leading-relaxed bg-transparent overflow-y-auto text-black placeholder-gray-500 dark:text-white dark:placeholder-neutral-500 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        rows={1}
        data-testid="chat-input"
        aria-label="Chat message input"
        aria-describedby={!canSend ? "chat-input-warning" : undefined}
      />
    </>
  );
};

export default ChatTextarea;
